/* eslint-disable prettier/prettier */
import { del, get, patch, post } from '@/infra/rest';
import {
  SendThirdPartyVendorEmailPayload,
  VendorIntakeFormData,
} from '@/modules/agent/types/vendorIntakeForm';
import { APIEndpoints } from '@/shared/constants/Endpoints';
import { QueryKeys } from '@/shared/constants/QueryKeys';
import { addNotification } from '@/shared/states/notification';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AllVendorsListResponse, ChatMessage, ChatMessagesApiResponse, GetMessagesResponse, PostUserMessageRequest, PostUserMessageResponse, Question, QuestionnaireSubmissionPayload, RespondOfflinePayload, RespondOfflineResponse, SubmitQuestionnaireFormPayload, SubmitQuestionnaireFormResponse, UserTextMessage, VendorDetails, VendorSessionQuestionnaireResponse } from '../types';

export const getVendorsList = async (): Promise<AllVendorsListResponse> => {
  const response = await get<AllVendorsListResponse>({
    url: APIEndpoints.FetchVendorsList,
  });
  return response;
};

const getVendorRiskLevel = (risk: string) => {
  switch (risk) {
    case 'Low':
      return 1;
    case 'Medium':
      return 2;
    case 'High':
      return 3;
    default:
      return 4;
  }
};

const getVendorStatusLevel = (risk: string) => {
  switch (risk) {
    case 'pending_review':
      return 1;
    case 'in_review':
      return 2;
    case 'reviewed':
      return 3;
    default:
      return 4;
  }
};

export const useGetVendorsListQuery = () => {
  const query = useQuery<VendorDetails[]>({
    queryKey: [QueryKeys.AllVendorsList],
    queryFn: async () => {
      try {
        const response = await getVendorsList();
        return response.data
          .map((vendorDetails, vendorIndex) => ({
            ...vendorDetails,
            vendor_product_name: vendorDetails.vendor_name.concat(
              ' ',
              vendorDetails.product_name
            ),
            uid: `vendor-${vendorIndex}`,
            risk_level: getVendorRiskLevel(vendorDetails.risk),
            status_level: getVendorStatusLevel(vendorDetails.status),
          }))
          .sort((a, b) => {
            const aTime = dayjs(a.last_assessed).isValid()
              ? dayjs(a.last_assessed).valueOf()
              : 0;
            const bTime = dayjs(b.last_assessed).isValid()
              ? dayjs(b.last_assessed).valueOf()
              : 0;
            return bTime - aTime;
          });
      } catch (error) {
        console.error('Error fetching vendors list:', error);
        addNotification({
          type: "error",
          title: "Error",
          message: "Failed to load vendors list. Please try again.",
        });
        throw error; // Re-throw to let React Query handle the error state
      }
    },
  });

  return query;
};

export const validateThirdPartyVendorFormToken = async (
  token: string
): Promise<any> => {
  try {
    const response = await get<any>({
      url: APIEndpoints.ValidateThirdPartyVendorToken,
      config: {
        params: {
          token,
        },
        headers: {
          token,
        },
        withCredentials: true,
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getVendorSessionQuestionnaire = async (): Promise<VendorSessionQuestionnaireResponse> => {
  try {
    const response = await get<VendorSessionQuestionnaireResponse>({
      url: APIEndpoints.VendorSessionQuestionnaire,
      config: {
        // Uses HTTP cookies for authentication (set by getEvidenceCollectionAuth)
        withCredentials: true,
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const submitThridPartyVendorIntakeForm = async (
  payload: VendorIntakeFormData
): Promise<any> => {
  const { token, ...data } = payload;
  const response = await post({
    url: APIEndpoints.SubmitThirdPartyVendorForm,
    data,
    config: {
      headers: {
        token,
      },
    },
  });
  return response;
};

export const useGetThirdPartyFormDetailsQuery = (token: string | null) => {
  return useQuery<any>({
    queryKey: [QueryKeys.ThirdPartyVendorToken, token],
    queryFn: async () => {
      if (token) {
        const response = await validateThirdPartyVendorFormToken(token);

        return response;
      } else {
        throw new Error('Invalid Token');
      }
    },
    enabled: !!token,
    retry: false,
  });
};

export const useSubmitVendorIntakeForm = () => {
  return useMutation({
    mutationFn: submitThridPartyVendorIntakeForm,
  });
};

export const sendThirdPartyVendorFormEmail = async (
  data: SendThirdPartyVendorEmailPayload
) => {
  const response = await post({
    url: APIEndpoints.SendThirdPartyVendorFormEmail,
    data,
  });
  return response;
};

export const useSendThirdPartyVendorFormEmail = () => {
  return useMutation({
    mutationFn: sendThirdPartyVendorFormEmail,
  });
};

// Define response types
type VendorIntakeFormLinkResponse = {
  success: boolean;
  message: string;
  data: {
    vendor_intake_form_url: string;
    expires_in: string;
  };
};

type DiscardAgentSessionResponse = {
  success: boolean;
  message: string;
  data: {
    agent_session_id: string;
  };
};

export const getVendorIntakeFormLink = async (data: {
  vendor_email: string;
  agent_session_id: string;
}): Promise<VendorIntakeFormLinkResponse> => {
  const response = await post({
    url: '/v0/rag/workflows/multi_file_va/vendor/intake-form-link',
    data,
  });
  return response as VendorIntakeFormLinkResponse;
};

export const useGetVendorIntakeFormLink = () => {
  return useMutation({
    mutationFn: getVendorIntakeFormLink,
  });
};

export const discardAgentSession = async (agentSessionId: string): Promise<DiscardAgentSessionResponse> => {
  const response = await del({
    url: `/v0/rag/workflows/multi_file_va/discard-session/${agentSessionId}`,
  });
  return response as DiscardAgentSessionResponse;
};

export const useDiscardAgentSession = () => {
  return useMutation({
    mutationFn: discardAgentSession,
  });
};

// Evidence Collection Types
export interface EvidenceDocument {
  document_name: string;
  id: string;
  max_number_of_files: number;
  number_of_files: number;
  required: boolean;
  source_urls: string[];
  file_names: string[];
  session_id: string;
  uploaded_by: string;
  uploaded_on: string;
}

export interface EvidenceCollectionResponse {
  success: boolean;
  message: string;
  assessor_name: string;
  requester_email: string;
  vendor_name: string;
  vendor_id: string;
  token_valid: boolean;
  questionnaire_json_url: string;
  questionnaire_excel_url: string;
  questionnaire_completion_status: string;
  evidence_documents: EvidenceDocument[];
  organization_name?: string; // Added from response headers
  chat_id?: string; // Chat ID if questionnaire status is followup_questions_generated or completed
  agent_session_id?: string; // Agent session ID
}

export const getEvidenceCollectionAuth = async (
  token: string
): Promise<EvidenceCollectionResponse> => {
  try {
    const response = await get<{ data: EvidenceCollectionResponse; headers: Record<string, string> }>({
      url: APIEndpoints.EvidenceCollectionAuth,
      config: {
        headers: {
          token,
        },
      },
      includeHeaders: true,
    });
    
    // Extract organization_name from response headers
    const organizationName = response?.headers?.['organization_name'] || response?.headers?.['Organization-Name'];

    return {
      ...response.data,
      organization_name: organizationName,
    };
  } catch (error: any) {
    // try extract organization name from various possible locations on the error
    const orgFromHeaders =
      error?.response?.headers?.['x-organization-name'] ||
      error?.headers?.['organization_name'] ||
      error?.headers?.['Organization-Name'];

    // attach organization_name to the error object before rejecting so callers can access it
    const rejectedError = error && typeof error === 'object' ? { ...error, organization_name: orgFromHeaders } : error;
    return Promise.reject(rejectedError);
  }
};

export const useGetEvidenceCollectionAuth = () => {
  return useMutation({
    mutationFn: getEvidenceCollectionAuth,
  });
};

// Evidence Upload Types
export interface EvidenceUploadPayload {
  evidence_documents: EvidenceDocument[];
}

export interface EvidenceUploadResponse {
  success: boolean;
  evidence_documents: EvidenceDocument[];
}

export const uploadEvidenceDocuments = async (
  token: string,
  payload: EvidenceUploadPayload
): Promise<EvidenceUploadResponse> => {
  try {
    const response = await post({
      url: APIEndpoints.EvidenceUpload,
      data: payload,
      config: {
        headers: {
          token,
        },
      },
    }) as EvidenceUploadResponse;
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useUploadEvidenceDocuments = () => {
  return useMutation({
    mutationFn: ({ token, payload }: { token: string; payload: EvidenceUploadPayload }) =>
      uploadEvidenceDocuments(token, payload),
  });
};

// Evidence Document Update/Delete
export interface PatchEvidencePayload {
  source_urls: string[];
}

export interface PatchEvidenceResponse {
  success: boolean;
  message: string;
  evidence_document?: EvidenceDocument;
}

export const patchEvidenceDocument = async (
  token: string,
  evidenceId: string,
  sourceUrls: string[]
): Promise<PatchEvidenceResponse> => {
  try {
    const response = await patch({
      url: `${APIEndpoints.PatchEvidence}/${evidenceId}`,
      config: {
        data: { source_urls: sourceUrls },
        headers: {
          token,
        },
      },
    }) as PatchEvidenceResponse;
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePatchEvidenceDocument = () => {
  return useMutation({
    mutationFn: ({ token, evidenceId, sourceUrls }: {
      token: string;
      evidenceId: string;
      sourceUrls: string[]
    }) => patchEvidenceDocument(token, evidenceId, sourceUrls),
  });
};

// Questionnaire Functions
export const fetchQuestionnaire = async (
  questionnaireUrl: string
): Promise<Question[]> => {
  try {
    const response = await fetch(questionnaireUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch questionnaire');
    }
    const data = await response.json();

    // Transform old format to new format for backward compatibility
    const transformedData = (data as any[]).map((question: any) => ({
      id: question.id || question['id'] || '',
      question: question.question || question['question'] || '',
      question_type: question.question_type || (
        question.type === 'free text' ? 'free_text' :
          question.type === 'single select' ? 'single_choice' :
            question.type === 'multi select' ? 'multiple_choice' :
              'free_text' // fallback
      ),
      is_dropdown: question.is_dropdown !== undefined ? question.is_dropdown : false,
      response_choices: question.response_choices || question['response choices'] || [],
      response_value: question.response_value || question['response value'] || (question.question_type === 'multiple_choice' || question.type === 'multi select' ? [] : ''),
      required: question.required !== undefined ? question.required : false,
      comments: question.comments || question['comments'] || '',
      questionnaire_id: question.questionnaire_id || question['questionnaire_id'] || '',
      placeholder_text: question.placeholder_text || question['placeholder_text'] || '',
      question_number: question.question_number || question['question number'] || 0,
      comments_allowed: question.comments_allowed !== undefined ? question.comments_allowed : false,
      parent_question_id: question.parent_question_id || null,
      parent_question_response_option: question.parent_question_response_option || null
    }));

    return transformedData as Question[];
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useGetQuestionnaire = (questionnaireUrl: string | null | undefined) => {
  return useQuery<Question[]>({
    queryKey: [QueryKeys.Questionnaire, questionnaireUrl],
    queryFn: () => {
      if (!questionnaireUrl) {
        throw new Error('Questionnaire URL is required');
      }
      return fetchQuestionnaire(questionnaireUrl);
    },
    enabled: !!questionnaireUrl,
    retry: 2,
  });
};

export const submitQuestionnaire = async (
  payload: QuestionnaireSubmissionPayload
): Promise<any> => {
  try {
    const { token, ...data } = payload;
    const response = await post({
      url: APIEndpoints.SubmitQuestionnaire,
      data,
      config: {
        headers: {
          token: token,
        },
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useSubmitQuestionnaire = () => {
  return useMutation({
    mutationFn: submitQuestionnaire,
  });
};

export const updateIndividualQuestion = async (
  questionId: string,
  token: string,
  questionData: Question
): Promise<Question> => {
  try {
    // Create full API URL for logging
    const apiUrl = APIEndpoints.UpdateIndividualQuestion.replace('{question_id}', questionId);


    // The patch function expects the data to be in the config object
    // In the rest/index.ts module, the patch function does not have a dedicated data parameter
    const response = await patch<Question>({
      url: apiUrl,
      config: {
        data: questionData,
        headers: {
          token: token, // Ensure token is included in request headers as lowercase
        },
      },
      // Important: Set useCookies to false as we're using custom token auth
      useCookies: false,
      isAuthRequired: false,
    });

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useUpdateIndividualQuestion = () => {
  return useMutation({
    mutationFn: ({ questionId, token, questionData }: {
      questionId: string;
      token: string;
      questionData: Question;
    }) => updateIndividualQuestion(questionId, token, questionData),
  });
};

export const respondOffline = async (
  token: string,
  payload: RespondOfflinePayload
): Promise<RespondOfflineResponse> => {
  try {
    const response = await post<RespondOfflinePayload, RespondOfflineResponse>({
      url: APIEndpoints.RespondOffline,
      data: payload,
      config: {
        headers: {
          token: token,
        },
      },
    });
    return response as RespondOfflineResponse;
  } catch (error) {
    console.error('Error submitting offline response:', error);
    return Promise.reject(error);
  }
};

export const useRespondOffline = () => {
  return useMutation({
    mutationFn: ({ token, payload }: {
      token: string;
      payload: RespondOfflinePayload;
    }) => respondOffline(token, payload),
  });
};

export const submitQuestionnaireForm = async (
  token: string,
  payload: SubmitQuestionnaireFormPayload
): Promise<SubmitQuestionnaireFormResponse> => {
  try {
    const response = await post<SubmitQuestionnaireFormPayload, SubmitQuestionnaireFormResponse>({
      url: APIEndpoints.SubmitQuestionnaireForm,
      data: payload,
      config: {
        headers: {
          token: token,
        },
      },
    });
    return response as SubmitQuestionnaireFormResponse;
  } catch (error) {
    console.error('Error submitting questionnaire form:', error);
    return Promise.reject(error);
  }
};

export const useSubmitQuestionnaireForm = () => {
  return useMutation({
    mutationFn: ({ token, payload }: {
      token: string;
      payload: SubmitQuestionnaireFormPayload;
    }) => submitQuestionnaireForm(token, payload),
  });
};



// Send Chat Message API
export const sendChatMessage = async (
  token: string,
  message: UserTextMessage
): Promise<PostUserMessageResponse> => {
  try {
    const requestPayload: PostUserMessageRequest = {
      chat_id: message.chat_id,
      message: {
        id: message.id,
        role: message.role,
        type: message.type,
        created_at: message.created_at,
        text: message.text,
        attachments: message.attachments
      }
    };
    
    const response = await post<PostUserMessageRequest, PostUserMessageResponse>({
      url: '/v0/tpra/chat/message',
      data: requestPayload,
      config: {
        headers: {
          token: token,
        },
      },
    });
    return response;
  } catch (error) {
    console.error('Error sending chat message:', error);
    return Promise.reject(error);
  }
};

export const useSendChatMessage = () => {
  return useMutation({
    mutationFn: ({ token, message }: {
      token: string;
      message: UserTextMessage;
    }) => sendChatMessage(token, message),
  });
};

export const useGetVendorSessionQuestionnaire = () => {
  return useMutation({
    mutationFn: getVendorSessionQuestionnaire,
  });
};

export const getChatMessages = async (chatId: string): Promise<GetMessagesResponse> => {
  try {
    const response = await get<ChatMessagesApiResponse>({
      url: `${APIEndpoints.ChatMessages}/${chatId}`,
      config: {
        // Uses HTTP cookies for authentication (set by getEvidenceCollectionAuth)
        withCredentials: true,
      },
    });
    
    // Transform API response to include chat_id in each message
    const transformedMessages: ChatMessage[] = response.data.messages.map((msg) => ({
      id: msg.id,
      chat_id: response.data.chat_id, // Add chat_id from response data
      role: msg.role,
      type: msg.type,
      created_at: msg.created_at,
      text: msg.text,
      attachments: msg.attachments || []
    }));
    
    return {
      success: response.success,
      messages: transformedMessages,
      total_count: response.data.total_count,
      chat_id: response.data.chat_id,
      session_id: response.data.session_id,
      organization_id: response.data.organization_id
    };
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useGetChatMessages = () => {
  return useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => getChatMessages(chatId),
  });
};

// Mark Questionnaire as Completed
export interface MarkQuestionnaireCompletedResponse {
  success: boolean;
  message: string;
}

export const markQuestionnaireCompleted = async (
  token: string
): Promise<MarkQuestionnaireCompletedResponse> => {
  try {
    const response = await post<{}, MarkQuestionnaireCompletedResponse>({
      url: APIEndpoints.MarkQuestionnaireCompleted,
      data: {},
      config: {
        headers: {
          token: token,
        },
      },
    });
    return response;
  } catch (error) {
    console.error('Error marking questionnaire as completed:', error);
    return Promise.reject(error);
  }
};

export const useMarkQuestionnaireCompleted = () => {
  return useMutation({
    mutationFn: (token: string) => markQuestionnaireCompleted(token),
  });
};
